/**
 * Shared logic for rendering a tool result from a backend render plan.
 *
 * The plan comes in two tiers:
 *   - the *catalogue* (`ui_table_plan`) is cached per tool and lists every field
 *     the tool can show;
 *   - the *view* (`ui_view`) rides on a single message and narrows that list when
 *     the user asked for specific fields.
 *
 * Keeping them separate is the whole point: a request like "just show key and
 * summary" must not become everyone's permanent layout, which is exactly what
 * happened under the previous single-tier design.
 */

const DEFAULT_COLUMN_COUNT = 5;

/**
 * Resolve a dotted path. Mirrors resolve_path() in the backend walker.
 * A `[]` segment collects the remainder across an array.
 */
export const resolvePath = (obj, path) => {
  if (!path) return obj;
  let cur = obj;
  for (const seg of path.split('.')) {
    if (cur === null || cur === undefined) return undefined;
    const collect = seg.endsWith('[]');
    const key = collect ? seg.slice(0, -2) : seg;
    if (Array.isArray(cur)) {
      const idx = Number(key);
      if (!Number.isInteger(idx)) return undefined;
      cur = cur[idx];
    } else if (typeof cur === 'object') {
      if (!(key in cur)) return undefined;
      cur = cur[key];
    } else {
      return undefined;
    }
    if (collect) return Array.isArray(cur) ? cur : undefined;
  }
  return cur;
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

// Keys worth trying, in order, when an object shows up where a scalar was
// expected -- a name beats a JSON blob if a field's value_path ever resolves
// to a container instead of a leaf.
const OBJECT_LABEL_KEYS = [
  'displayName',
  'name',
  'label',
  'title',
  'value',
  'key',
  'email',
];

const describeObject = (value) => {
  for (const k of OBJECT_LABEL_KEYS) {
    if (typeof value[k] === 'string' && value[k]) return value[k];
  }
  return null;
};

/**
 * Turn a raw value into display text using the field's declared type.
 * Returns null for "nothing to show", so callers can render a dim placeholder
 * rather than the shouty "N/A" the old table printed in every empty cell.
 */
export const formatValue = (value, field) => {
  if (value === null || value === undefined || value === '') return null;

  const type = field?.type || 'string';
  const aggregate = field?.aggregate;

  const describeOrStringify = (v) => describeObject(v) ?? JSON.stringify(v);

  if (Array.isArray(value)) {
    const items = value.filter((v) => v !== null && v !== undefined);
    if (items.length === 0) return null;
    if (aggregate === 'count' || type === 'object[]')
      return String(items.length);
    if (aggregate === 'first') {
      const first = items[0];
      return typeof first === 'object'
        ? describeOrStringify(first)
        : String(first);
    }
    return items
      .map((v) => (typeof v === 'object' ? describeOrStringify(v) : String(v)))
      .join(', ');
  }

  switch (type) {
    case 'date':
      return formatDate(value);
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    default:
      // A field typed as a scalar that turns out to hold an object means the
      // catalogue's value_path pointed one level too high — see the comment on
      // describeObject. Prefer a human label over a JSON dump.
      if (typeof value === 'object') return describeOrStringify(value);
      return String(value);
  }
};

/**
 * Accept either plan generation and return the v2 shape.
 *
 * Historical chat messages still carry v1 plans (`is_table_displayable` plus a
 * flat `columns` array). They must keep rendering — a schema change should not
 * blank out tables in conversations people already had.
 */
export const normalizePlan = (plan) => {
  if (!plan) return null;

  if (Array.isArray(plan.fields)) return plan; // already v2

  if (plan.is_table_displayable && Array.isArray(plan.columns)) {
    const fields = plan.columns.map((c, i) => ({
      id: `c${i}`,
      label: c.header,
      value_path: c.value_path,
      type: 'string',
    }));
    return {
      display_type: 'table',
      array_path: plan.array_path,
      object_path: null,
      row_url_path: null,
      fields,
      default_columns: fields.map((f) => f.id),
      _legacy: true,
    };
  }

  return null;
};

/**
 * Resolve which fields to actually render.
 *
 *   ui_view.columns  ->  plan.default_columns  ->  first N fields
 *
 * then drop any field whose path resolves in none of the rows. That last step
 * matters because the backend prunes large payloads before sending them: a field
 * can be legitimately catalogued yet absent from this particular response, and
 * showing an empty column for it looks like a bug.
 */
export const resolveFields = (plan, uiView, rows) => {
  if (!plan?.fields?.length) return [];

  const byId = new Map(plan.fields.map((f) => [f.id, f]));

  let ids = null;
  if (Array.isArray(uiView?.columns) && uiView.columns.length) {
    ids = uiView.columns.filter((id) => byId.has(id));
  }
  if (!ids?.length && Array.isArray(plan.default_columns)) {
    ids = plan.default_columns.filter((id) => byId.has(id));
  }
  if (!ids?.length) {
    ids = plan.fields.slice(0, DEFAULT_COLUMN_COUNT).map((f) => f.id);
  }

  const sample = (rows || []).slice(0, 25);
  return ids
    .map((id) => byId.get(id))
    .filter(Boolean)
    .filter((f) => {
      if (!sample.length) return true;
      return sample.some((row) => resolvePath(row, f.value_path) !== undefined);
    });
};

/** The rows a table should render, or [record] for a detail view. */
export const resolveRows = (plan, data) => {
  if (!plan || !data) return [];
  if (plan.display_type === 'detail') {
    const rec = resolvePath(data, plan.object_path || '');
    return rec && typeof rec === 'object' ? [rec] : [];
  }
  const rows = resolvePath(data, plan.array_path || '');
  return Array.isArray(rows) ? rows : [];
};

/** Per-row link target, declared by the plan rather than guessed from key names. */
export const resolveRowUrl = (plan, row) => {
  if (!plan?.row_url_path) return null;
  const v = resolvePath(row, plan.row_url_path);
  return typeof v === 'string' && v.startsWith('http') ? v : null;
};

/** Shared dark-surface tokens, so table and detail cannot drift apart. */
export const surface = {
  bg: '#18181b',
  border: '#3f3f46',
  rowBorder: '#27272a',
  headBg: '#27272a',
  text: '#d4d4d8',
  muted: '#a1a1aa',
  dim: '#52525b',
};
